/**
 * PostHog identify/reset helpers.
 *
 * Mirrors the GA4 identifier lifecycle (SPEC-027 D2): identify the person on
 * login, reset on logout, so events are attributed correctly. Every call
 * no-ops until PostHog has actually initialized (dev builds, or PROD_ENV off,
 * or the key absent), so importing this module is always safe.
 */
import posthog from 'posthog-js'

// posthog.__loaded is set to true once posthog.init has run. Calling identify
// or reset before then just logs a warning, so we guard on it to stay quiet.
// eslint-disable-next-line no-underscore-dangle
const isReady = () => posthog && posthog.__loaded

export const identifyUser = (user) => {
  if (!isReady() || !user || user.id == null) return
  posthog.identify(String(user.id), {
    email: user.email,
    role: user.role
  })
}

export const resetPosthog = () => {
  if (!isReady()) return
  posthog.reset()
}

// Generic event capture. Mirrors pushGa4Event's role as the single chokepoint,
// but for PostHog: given a resolved event name and a flat properties bag it
// forwards to posthog.capture, no-opping until PostHog is initialized.
export const capturePosthogEvent = (name, properties = {}) => {
  if (!isReady() || !name) return
  posthog.capture(name, properties)
}

// Feature flags. All guarded so they return safe defaults until PostHog loads.
export const isFeatureEnabled = (flag) =>
  isReady() ? posthog.isFeatureEnabled(flag) : false

export const getFeatureFlag = (flag) =>
  isReady() ? posthog.getFeatureFlag(flag) : undefined

// Subscribe to flag load/refresh. Returns an unsubscribe function (a no-op when
// PostHog is not initialized) so React effects can clean up uniformly.
export const onFeatureFlags = (callback) =>
  isReady() ? posthog.onFeatureFlags(callback) : () => {}
